import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { useConsultation } from "@/components/forms/ConsultationDialog";
import { TRIGGERS, IMAGES } from "@/data/content";

export default function WhatChanged() {
  const [active, setActive] = useState(TRIGGERS[0].id);
  const { open } = useConsultation();
  const current = TRIGGERS.find((t) => t.id === active);

  return (
    <>
      <PageHero
        kicker="Situational Triggers"
        title="We meet you exactly where your technology pain is."
        subtitle="Certain moments carry real career consequences. Pick what changed and we'll generate a custom checklist to help you avoid the traps."
        image={IMAGES.heroBokeh}
      />

      <section className="section-x py-16 sm:py-24">
        <div className="container-tmt">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            {/* Trigger selector */}
            <div className="space-y-3">
              {TRIGGERS.map((t) => {
                const Icon = t.icon;
                const isActive = t.id === active;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActive(t.id)}
                    className={`flex w-full items-center gap-4 rounded-2xl border p-5 text-left transition-colors duration-300 ${
                      isActive ? "border-primary/50 bg-primary/10" : "border-border bg-card hover:border-primary/30"
                    }`}
                  >
                    <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-semibold">{t.title}</h3>
                      <p className="mt-0.5 text-sm text-muted-foreground line-clamp-1">{t.summary}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Checklist panel */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="relative overflow-hidden rounded-3xl border border-primary/25 bg-gradient-surface p-8 shadow-elegant sm:p-10"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
                  <span className="kicker">Your custom checklist</span>
                  <h2 className="mt-3 font-display text-3xl font-semibold">{current.title}</h2>
                  <p className="mt-3 text-muted-foreground">{current.summary}</p>

                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                    <p className="text-sm text-foreground/90">Move too fast here and you inherit the traps: missing exit ramps, uncapped price hikes, and dead-clause renewals.</p>
                  </div>

                  <ul className="mt-6 space-y-3">
                    {current.checklist.map((c) => (
                      <li key={c} className="flex items-start gap-3 rounded-xl border border-border bg-card/60 px-4 py-3">
                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                        <span className="text-sm text-foreground/90">{c}</span>
                      </li>
                    ))}
                  </ul>

                  <Button variant="hero" size="lg" className="mt-8 w-full" onClick={open}>
                    Get the Full Playbook for “{current.title}” <ArrowRight className="h-4 w-4" />
                  </Button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
